/**
 * @fileoverview Real-time WebSocket communication client for the Buzzer Battle Fan dynamic ("De barrio en barrio").
 * Manages WebSocket connection lifecycle, automatic reconnection, state synchronization,
 * and option selection dispatching from the fan's mobile view (/buzzer).
 * 
 * @module utilities/WS/BuzzerBattleFanWS
 */

let buzzerBattleFanWebSocket = null;
let isBuzzerBattleFanConnected = false;
let reconnectionTimeoutIdentifier = null;
let reconnectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 20;
const RECONNECTION_DELAY_MILLISECONDS = 2500;

/**
 * Establishes a real-time WebSocket connection for the Buzzer Battle fan mobile view.
 * 
 * @function ConnectWebSocketForBuzzerBattleFan
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked upon receiving parsed JSON packets.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked upon connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectWebSocketForBuzzerBattleFan = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (buzzerBattleFanWebSocket || isBuzzerBattleFanConnected) {
        return;
    }

    const establishConnection = () => {
        if (reconnectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        reconnectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=overlay:buzzer-battle:fan`;
            buzzerBattleFanWebSocket = new WebSocket(webSocketEndpointUrl);

            buzzerBattleFanWebSocket.onopen = () => {
                isBuzzerBattleFanConnected = true;
                reconnectionAttemptCounter = 0;
                onStatusChange('connected');

                // Request initial snapshot of the game state
                RequestBuzzerBattleFanState();
            };

            buzzerBattleFanWebSocket.onclose = () => {
                isBuzzerBattleFanConnected = false;
                buzzerBattleFanWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            buzzerBattleFanWebSocket.onerror = () => {
                onStatusChange('error');
            };

            buzzerBattleFanWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or malformed packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Buzzer Battle fan WebSocket connection.
 * 
 * @function SendBuzzerBattleFanMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendBuzzerBattleFanMessage = (payloadObject) => {
    try {
        if (
            buzzerBattleFanWebSocket &&
            buzzerBattleFanWebSocket.readyState === WebSocket.OPEN
        ) {
            buzzerBattleFanWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (dispatchError) {
        return false;
    }
};

/**
 * Sends the selected answer option from the fan's mobile pulser.
 * 
 * @function SendBuzzerBattleOptionSelection
 * @param {number|string} optionIdentifier - Selected option ID.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendBuzzerBattleOptionSelection = (optionIdentifier) => {
    const parsedOptionId = Number(optionIdentifier);
    if (!parsedOptionId || isNaN(parsedOptionId) || parsedOptionId <= 0) {
        return false;
    }

    return SendBuzzerBattleFanMessage({
        type: 'buzzer_select_option',
        optionId: parsedOptionId,
        data: {
            optionId: parsedOptionId
        }
    });
};

/**
 * Emits buzzer press signal from mobile view.
 * 
 * @function SendBuzzerBattlePress
 * @param {string} playerIdentifier - Player ID / keycode.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendBuzzerBattlePress = (playerIdentifier) => {
    return SendBuzzerBattleFanMessage({
        type: 'buzzer_press',
        playerId: playerIdentifier,
        data: {
            playerId: playerIdentifier
        }
    });
};

/**
 * Requests the current game state snapshot from the server.
 * 
 * @function RequestBuzzerBattleFanState
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const RequestBuzzerBattleFanState = () => {
    return SendBuzzerBattleFanMessage({
        type: 'buzzer_get_state'
    });
};

/**
 * Cleanly disconnects and terminates the Buzzer Battle fan WebSocket connection.
 * 
 * @function DisconnectWebSocketForBuzzerBattleFan
 */
export const DisconnectWebSocketForBuzzerBattleFan = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (buzzerBattleFanWebSocket) {
            buzzerBattleFanWebSocket.onopen = null;
            buzzerBattleFanWebSocket.onclose = null;
            buzzerBattleFanWebSocket.onerror = null;
            buzzerBattleFanWebSocket.onmessage = null;

            if (buzzerBattleFanWebSocket.readyState === WebSocket.OPEN) {
                buzzerBattleFanWebSocket.close();
            } else if (buzzerBattleFanWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = buzzerBattleFanWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            buzzerBattleFanWebSocket = null;
            isBuzzerBattleFanConnected = false;
            reconnectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
